import { Link } from "react-router-dom";
import { Lock, Sparkles, AlertTriangle, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export const CREDIT_HELP = "AI credits measure your school's Ask Jonny usage. A typical question uses only a small fraction of a credit.";

const credits = (n) => Number(n || 0).toFixed(2);

export function LockedScreen() {
  const { user, logout } = useAuth();
  const contact = user.billing?.contact;
  return (
    <div className="min-h-screen grid place-items-center p-6 lab-grid" data-testid="trial-locked-screen">
      <div className="max-w-md w-full bg-card border rounded-2xl p-8 shadow-sm text-center space-y-4">
        <div className="mx-auto h-12 w-12 rounded-full bg-amber-500/15 text-amber-600 grid place-items-center"><Lock className="h-6 w-6" /></div>
        <h1 className="text-2xl font-bold">Your free trial has ended</h1>
        <p className="text-sm text-muted-foreground">Thanks for trying Ask Jonny at {user.school?.name}. Your school's data is safe. To keep using the app, contact us to subscribe.</p>
        <a href={`mailto:${contact}?subject=${encodeURIComponent(`Subscribe ${user.school?.name || ""} to Ask Jonny`)}`} className="block" data-testid="trial-locked-contact">
          <Button className="w-full h-11">Contact {contact}</Button>
        </a>
        <Button variant="ghost" onClick={logout} data-testid="trial-locked-logout"><LogOut className="h-4 w-4 mr-2" />Sign out</Button>
      </div>
    </div>
  );
}

export function BillingBanner() {
  const { user } = useAuth();
  const b = user?.billing;
  if (!b || user.superadmin && !b.ai_blocked && b.plan !== "trial") return null;
  if (b.out_of_credit) {
    return (
      <div className="flex flex-wrap items-center gap-2 px-4 py-2 text-sm bg-amber-500/15 border-b border-amber-500/30" data-testid="billing-banner-no-credit">
        <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
        <span>Your school has used all its AI credits, so Jonny is paused.{b.plan === "trial" ? " The rest of the app still works until your trial ends." : " Everything else still works."}</span>
        <a className="font-semibold underline" href={`mailto:${b.contact}`} data-testid="billing-banner-contact">Contact {b.contact} to subscribe</a>
      </div>
    );
  }
  if (b.plan !== "trial") return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-1 text-xs bg-primary/10 border-b border-primary/20" data-testid="billing-banner-trial" title={CREDIT_HELP}>
      <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
      <span><b>Free trial</b> · {b.days_left} day{b.days_left === 1 ? "" : "s"} left</span>
      {b.credit_limit != null && <span data-testid="billing-banner-credits">{credits(b.credits_used)} of {b.credit_limit} AI credits used</span>}
      <span className="text-muted-foreground hidden md:inline">{CREDIT_HELP}</span>
    </div>
  );
}

export function AuthLinks({ forgotLabel = "Forgot password?", registerLabel = "Register your school" }) {
  return (
    <div className="flex justify-between text-xs mt-4">
      <Link to="/forgot-password" className="text-primary hover:underline" data-testid="forgot-password-link">{forgotLabel}</Link>
      <Link to="/signup" className="text-primary hover:underline" data-testid="signup-link">{registerLabel}</Link>
    </div>
  );
}
